import { env } from "@shared/config/env"
import { detectPlatform, detectStore } from "@shared/lib/platform"
import { getTelegramUserId } from "@shared/lib/telegram"
import posthog, { type CaptureResult } from "posthog-js"
import { captureCapacitorWebVitals } from "./web-vitals"

/** Форма элемента `$exception_list`: PostHog типизирует properties как `any`. */
type CapturedException = {
	value?: string
	mechanism?: { handled?: boolean; synthetic?: boolean }
	stacktrace?: { frames?: unknown[] }
}

/**
 * Браузер подменяет исключение из кросс-доменного скрипта (без CORS)
 * на обезличенное "Script error." без стека и сообщения.
 * Чинить в таком событии нечего — не отправляем его в PostHog.
 */
function isOpaqueCrossOriginScriptError(event: CaptureResult): boolean {
	if (event.event !== "$exception") return false

	const exceptions: CapturedException[] = event.properties.$exception_list
	if (!Array.isArray(exceptions) || exceptions.length !== 1) return false

	const [exception] = exceptions
	return (
		exception?.value === "Script error." &&
		exception.mechanism?.handled === false &&
		exception.mechanism?.synthetic === true &&
		!exception.stacktrace?.frames?.length
	)
}

function dropNoiseEvents(event: CaptureResult): CaptureResult | null {
	if (isOpaqueCrossOriginScriptError(event)) return null
	return event
}

function restoreNativePathname(event: CaptureResult): CaptureResult {
	const [, hashPath] = String(event.properties.$current_url).split("#")
	if (hashPath?.startsWith("/")) {
		event.properties.$pathname = hashPath.split("?")[0]
	}
	return event
}

const platform = detectPlatform()

const deviceProperties = {
	platform,
	store: detectStore(),
	app_version: env.VITE_PUBLIC_APP_VERSION,
}

function stampDeviceProperties(event: CaptureResult): CaptureResult {
	Object.assign(event.properties, deviceProperties)

	const telegramUserId = getTelegramUserId()
	if (telegramUserId) event.properties.telegram_user_id = telegramUserId

	return event
}

posthog.init(env.VITE_PUBLIC_POSTHOG_KEY, {
	api_host: env.VITE_PUBLIC_POSTHOG_HOST,
	ui_host: "https://eu.posthog.com",
	capture_pageview: false,
	capture_pageleave: true,
	capture_exceptions: true,
	// На native хэш — это маршрут (hash-history), его оставляем. В Telegram и
	// браузере маршрут в pathname, а в хэше Telegram передаёт launch-параметры:
	// SDK вырезает их из всех URL, включая replay и heatmaps.
	disable_capture_url_hashes: platform !== "ios" && platform !== "android",
	autocapture: {
		dom_event_allowlist: ["click", "submit"],
		css_selector_ignorelist: [
			".ph-no-autocapture",
			"[data-ph-no-autocapture]",
			"textarea",
			'input:not([type="checkbox"]):not([type="radio"])',
		],
	},
	before_send: [dropNoiseEvents, restoreNativePathname, stampDeviceProperties],
})

captureCapacitorWebVitals()
